'use client'

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Row,
  type RowData,
  type RowModel,
  useReactTable,
} from '@tanstack/react-table'
import { GripVertical } from 'lucide-react'
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table/table'
import { cn } from '@/lib/cn'

type RowDataWithId = RowData & {
  id: string
  disabled?: boolean
}

type DraggableDataTableProps<TData extends RowDataWithId> = {
  columns: ColumnDef<TData>[]
  data: TData[]
  loading?: boolean
  emptyDataContainer?: ReactNode
  onDragEndUpdate?: (data: TData[]) => void
}

const RowDragHandCell = ({
  rowId,
  disabled,
}: {
  rowId: string
  disabled?: boolean
}) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  })

  if (disabled) {
    return (
      <TableCell className={cn('w-fit border-none cursor-not-allowed')}>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('text-system-text-primary cursor-not-allowed')}
          disabled={disabled}
        >
          <GripVertical />
        </Button>
      </TableCell>
    )
  }

  return (
    <TableCell
      className={cn('w-fit border-none cursor-move')}
      {...listeners}
      {...attributes}
    >
      <Button
        size="icon"
        variant="ghost"
        className={cn('text-system-text-primary cursor-move')}
        disabled={disabled}
        type="button"
      >
        <GripVertical />
      </Button>
    </TableCell>
  )
}
RowDragHandCell.displayName = 'RowDragHandCell'

const DraggableRow = <TData extends RowDataWithId>({
  row,
}: {
  row: Row<TData>
}) => {
  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={cn(
        'text-sm font-medium',
        row.original.disabled
          ? 'text-system-grey-strong'
          : 'text-system-text-primary',
      )}
    >
      <RowDragHandCell
        rowId={row.id}
        disabled={row.original?.disabled || false}
      />
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id} className="border-none p-4">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  )
}
DraggableRow.displayName = 'DraggableRow'

const DataTableBody = <TData extends RowDataWithId>({
  columns,
  loading,
  rowModel,
  sortableContextItems,
  emptyDataContainer,
}: Pick<
  DraggableDataTableProps<TData>,
  'loading' | 'columns' | 'emptyDataContainer'
> & {
  rowModel: RowModel<TData>
  sortableContextItems: string[]
}) => {
  if (loading) {
    return (
      <TableRow>
        {Array.from({ length: columns.length }).map((_, index) => (
          <TableCell key={index} className="p-4">
            <Skeleton className="h-6 w-full" />
          </TableCell>
        ))}
      </TableRow>
    )
  }

  if (!rowModel.rows.length)
    return (
      <TableRow>
        <TableCell
          colSpan={columns.length}
          className={cn(!emptyDataContainer && 'h-24 text-center')}
        >
          {emptyDataContainer ?? (
            <span className="text-lg font-bold text-system-text-secondary">
              Sem dados
            </span>
          )}
        </TableCell>
      </TableRow>
    )

  return (
    <SortableContext
      items={sortableContextItems}
      strategy={verticalListSortingStrategy}
    >
      {rowModel.rows.map((row) => (
        <DraggableRow key={row.id} row={row} />
      ))}
    </SortableContext>
  )
}
DataTableBody.displayName = 'DataTableBody'

const DraggableDataTable = <TData extends RowDataWithId>({
  columns,
  data: initialData,
  loading,
  emptyDataContainer,
  onDragEndUpdate,
}: DraggableDataTableProps<TData>) => {
  const [data, setData] = useState<TData[]>(initialData)

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor),
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) return

      const oldIndex = data.findIndex((item) => item.id === active.id)
      const newIndex = data.findIndex((item) => item.id === over.id)
      const newData = arrayMove(data, oldIndex, newIndex)

      const updatedData = newData.map((item, index) => ({
        ...item,
        position: index + 1,
      }))

      setData(updatedData)
      if (onDragEndUpdate) onDragEndUpdate(updatedData)
    },
    [data, onDragEndUpdate],
  )

  useEffect(() => {
    setData(initialData)
  }, [initialData])

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <Table>
        <TableHeader>
          <TableRow>
            {!loading && !!data.length && (
              <TableHead className="border-none p-4" />
            )}
            {table.getHeaderGroups().map((headerGroup) =>
              headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  className="w-fit border-none p-4 text-sm font-medium text-system-text-secondary"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              )),
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          <DataTableBody
            columns={columns}
            rowModel={table.getRowModel()}
            emptyDataContainer={emptyDataContainer}
            sortableContextItems={data.map((item) => item.id)}
            loading={loading}
          />
        </TableBody>
      </Table>
    </DndContext>
  )
}
DraggableDataTable.displayName = 'DraggableDataTable'

export { DraggableDataTable }
